import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Snackbar,
} from '@mui/material';
import { Close as CloseIcon, Add as AddIcon } from '@mui/icons-material';
import { api } from '../services/api';
import DateRangeSelector from './DateRangeSelector';

interface CustomInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  onInvoiceCreated: (invoice: any) => void;
}

const CustomInvoiceModal: React.FC<CustomInvoiceModalProps> = ({
  open,
  onClose,
  onInvoiceCreated,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [userCurrency, setUserCurrency] = useState<{currency?: string, billing_currency?: string} | null>(null);
  
  const [formData, setFormData] = useState({
    period_start: new Date().toISOString().split('T')[0].substring(0, 7) + '-01', // First day of current month
    period_end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0], // Last day of current month
    notes: '',
    conversion_rate: '',
  });
  const [dateRangeErrors, setDateRangeErrors] = useState<{start?: string, end?: string}>({});
  const [toastNotification, setToastNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Load user profile when modal opens
  useEffect(() => {
    if (open) {
      loadUserProfile();
    }
  }, [open]);

  const loadUserProfile = async () => {
    try {
      const response = await api.get('/profile');
      if (response.data.success) {
        const user = response.data.data.user;
        setUserCurrency({
          currency: user.currency || 'USD',
          billing_currency: user.billing_currency || 'USD'
        });
      }
    } catch (err: any) {
      console.error('Failed to load user profile:', err);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear messages when user starts typing
    if (error) setError(null);
    if (success) setSuccess(null);
    
    // Clear date range errors when dates change
    if (field === 'period_start' || field === 'period_end') {
      setDateRangeErrors({});
    }
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);

    // Basic validation
    if (!formData.period_start || !formData.period_end) {
      setError('Please fill in all required fields');
      return;
    }

    if (new Date(formData.period_end) <= new Date(formData.period_start)) {
      setError('End date must be after start date');
      return;
    }

    try {
      setLoading(true);
      
      const payload: any = {
        period_start: formData.period_start,
        period_end: formData.period_end,
        notes: formData.notes,
      };
      
      // Include conversion_rate if provided
      if (formData.conversion_rate && parseFloat(formData.conversion_rate) > 0) {
        payload.conversion_rate = parseFloat(formData.conversion_rate);
      }
      
      const response = await api.post('/vendor-invoices/create-custom', payload);
      
      if (response.data.success) {
        setSuccess('Custom invoice created successfully!');
        onInvoiceCreated(response.data.data);
        
        // Close modal after a brief delay to show success message
        setTimeout(() => {
          onClose();
          resetForm();
        }, 1500);
      }
    } catch (err: any) {
      console.error('Error creating custom invoice:', err);
      
      // Handle date range validation errors
      if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        if (errors.period_start || errors.period_end) {
          setDateRangeErrors({
            start: errors.period_start?.[0],
            end: errors.period_end?.[0]
          });
          
          // Show toast notification for date range conflict
          if (err.response?.data?.message?.includes('date range')) {
            handleToastNotification(err.response.data.message, 'warning');
          }
        }
      }
      
      setError(err.response?.data?.message || 'Failed to create custom invoice');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      period_start: new Date().toISOString().split('T')[0].substring(0, 7) + '-01',
      period_end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
      notes: '',
      conversion_rate: '',
    });
    setError(null);
    setSuccess(null);
    setDateRangeErrors({});
  };

  const handleToastNotification = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setToastNotification({
      open: true,
      message,
      severity
    });
  };

  const handleCloseToast = () => {
    setToastNotification(prev => ({ ...prev, open: false }));
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      resetForm();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon color="primary" />
          <Typography variant="h6" component="span">
            Create Custom Invoice
          </Typography>
        </Box>
        <IconButton onClick={handleClose} disabled={loading} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Create a new invoice without estimates. You'll be able to add custom items after creation.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <DateRangeSelector
            periodStart={formData.period_start}
            periodEnd={formData.period_end}
            onPeriodStartChange={(value) => handleInputChange('period_start', value)}
            onPeriodEndChange={(value) => handleInputChange('period_end', value)}
            disabled={loading}
            showValidation={true}
            showToastNotifications={true}
            error={dateRangeErrors}
            onToastNotification={handleToastNotification}
            helperText={{
              start: 'First day of the billing period',
              end: 'Last day of the billing period'
            }}
          />

          <TextField
            label="Notes (Optional)"
            multiline
            rows={3}
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            fullWidth
            disabled={loading}
            placeholder="Any additional notes or comments for this invoice..."
            helperText="Optional notes that will appear on the invoice"
          />

          {/* Show conversion rate input if currencies differ */}
          {userCurrency && 
           userCurrency.currency !== userCurrency.billing_currency && (
            <TextField
              label={`Conversion Rate (${userCurrency.currency} to ${userCurrency.billing_currency})`}
              type="number"
              value={formData.conversion_rate}
              onChange={(e) => handleInputChange('conversion_rate', e.target.value)}
              inputProps={{ min: 0.0001, step: 0.0001 }}
              helperText={`Enter the conversion rate from ${userCurrency.currency} to ${userCurrency.billing_currency}`}
              fullWidth
              disabled={loading}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          color="primary"
          startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
          sx={{ minWidth: 120 }}
        >
          {loading ? 'Creating...' : 'Create Invoice'}
        </Button>
      </DialogActions>

      {/* Toast Notification */}
      <Snackbar
        open={toastNotification.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseToast} 
          severity={toastNotification.severity} 
          sx={{ width: '100%' }}
        >
          {toastNotification.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default CustomInvoiceModal;
